import { useEffect, useState } from 'react';

import {
  TrustEyeOffIcon,
  TrustGlobeIcon,
  TrustShieldIcon,
} from './landing/icons';
import { NaiaLogo } from '../components/header/icons';
import { Features } from './landing/features';
import { HowItWorks } from './landing/how-it-works';
import { SectionLabel } from './landing/section-label';
import { TrustBar } from './landing/trust-bar';
import './landing.css';
import { Link } from '../lib/routing';

/* Scroll-triggered entrance animations — adds .is-visible to [data-animate] elements */
function useScrollAnimation() {
  useEffect(() => {
    if (!('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('is-visible'); observer.unobserve(e.target); } }),
      { threshold: 0.1, rootMargin: '0px 0px -48px 0px' },
    );
    const timer = setTimeout(() => {
      document.querySelectorAll('[data-animate]').forEach(el => observer.observe(el));
    }, 80);
    return () => { clearTimeout(timer); observer.disconnect(); };
  }, []);
}

export default function Landing() {
  useScrollAnimation();
  return (
    <>
      <LandingNav />
      <main className="landing-main">
        <Hero />
        <TrustBar />
        <Features />
        <HowItWorks />
        <ForWhom />
        <SecuritySection />
        <DownloadCTA />
      </main>
      <LandingFooter />
    </>
  );
}

/* ── Nav ─────────────────────────────────────────────────────────────── */
function LandingNav() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className={`landing-nav${scrolled ? ' scrolled' : ''}`}>
      <div className="landing-container landing-header-inner">
        <Link to="/" aria-label="Naïa-Edu accueil" className="landing-brand">
          <NaiaLogo height={28} onDark />
        </Link>
        <nav className="landing-nav">
          <Link to="/sign-in" className="nav-link-ghost">Se connecter</Link>
          <Link to="/sign-in" className="nav-btn-primary">Parler avec Naïa</Link>
        </nav>
      </div>
    </header>
  );
}

/* ── Hero ────────────────────────────────────────────────────────────── */
function Hero() {
  return (
    <section className="hero-section">
      <div className="hero-glow" />
      <div className="hero-grid-bg" aria-hidden />
      <div className="landing-container hero-inner">
        <div className="hero-text">
          <div className="hero-badge">✦ IA pédagogique · De la maternelle au lycée</div>
          <h1 className="hero-title">
            Le tuteur IA<br />
            qui rend chaque élève<br />
            <span className="hero-accent">curieux.</span>
          </h1>
          <p className="hero-sub">
            Naïa accompagne chaque élève — de la maternelle au lycée — dans ses questions, ses devoirs et sa curiosité.
            Sans publicité, sans algorithme de rétention. Juste de l'apprentissage.
          </p>
          <div className="hero-ctas">
            <Link to="/sign-in" className="cta-primary cta-on-dark">
              Parler avec Naïa →
            </Link>
          </div>
          <p className="hero-footnote">Gratuit · Aucune carte bancaire requise</p>
        </div>
        <div className="hero-visual" aria-hidden>
          <ChatMockup />
        </div>
      </div>
    </section>
  );
}

/* ── Features ────────────────────────────────────────────────────────── */
/* ── How it works ────────────────────────────────────────────────────── */
/* ── For whom ────────────────────────────────────────────────────────── */
function ForWhom() {
  const personas = [
    {
      cls: 'persona-eleve', icon: '🎒', title: 'Élèves',
      desc: 'Naïa répond à n\'importe quelle question, explique les leçons avec des mots simples, invente des histoires et corrige les devoirs sans jamais faire à la place.',
      items: ['Questions à l\'oral ou par écrit', 'Exercices interactifs', 'Mode voix intégré'],
    },
    {
      cls: 'persona-parent', icon: '👪', title: 'Parents',
      desc: 'Vous gardez le contrôle total. Pas de contenu inapproprié, pas de publicité, pas de temps d\'écran subi. Naïa est une aide aux devoirs, pas un réseau social.',
      items: ['Contenu entièrement filtré', 'Pas d\'accès à internet libre', 'Historique des conversations'],
    },
    {
      cls: 'persona-teacher', icon: '👩‍🏫', title: 'Enseignants',
      desc: 'Suivez la progression de vos élèves en temps réel, créez des parcours personnalisés et assignez des exercices. Naïa complète votre enseignement, jamais ne le remplace.',
      items: ['Tableau de bord de classe', 'Parcours sur mesure', 'Aligné sur les programmes'],
    },
  ];
  return (
    <section className="section-light">
      <div className="landing-container">
        <div data-animate="">
          <SectionLabel>Pour qui</SectionLabel>
          <h2 className="section-title">Un outil pour toute l'équipe pédagogique.</h2>
        </div>
        <div className="personas-grid">
          {personas.map((p, i) => (
            <div key={p.title} className={`persona-card ${p.cls}`} data-animate="" data-delay={String(i * 120)}>
              <span className="persona-icon">{p.icon}</span>
              <h3>{p.title}</h3>
              <p>{p.desc}</p>
              <ul className="persona-list">
                {p.items.map(it => (
                  <li key={it}>
                    <span className="persona-check" aria-hidden>✓</span>
                    {it}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Security ────────────────────────────────────────────────────────── */
function SecuritySection() {
  return (
    <section className="section-dark">
      <div className="landing-container security-inner">
        <div data-animate="from-left">
          <SectionLabel light>Confiance & sécurité</SectionLabel>
          <h2 className="section-title landing-title-light">Vos données restent les vôtres.</h2>
          <p className="section-sub landing-sub-light">
            Naïa-Edu est construit autour d'un principe simple : les données des élèves et des enseignants ne valent rien commercialement.
            Pas de profil publicitaire, pas de revente, pas de traque.
          </p>
          <div className="security-badges">
            {[
              { icon: <TrustGlobeIcon size={22} />, label: 'Hébergement UE', sub: 'Base et fichiers dans l’UE' },
              { icon: <TrustShieldIcon size={22} />,label: 'No Ads',         sub: 'Aucune publicité' },
              { icon: <TrustEyeOffIcon size={22} />,label: 'No Tracking',    sub: 'Zéro traque' },
            ].map((b, i) => (
              <div key={b.label} className="security-badge" data-animate="" data-delay={String(i * 80)}>
                <span className="security-badge-icon">{b.icon}</span>
                <span className="security-badge-label">{b.label}</span>
                <span className="security-badge-sub">{b.sub}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="security-illustration" aria-hidden data-animate="from-right">
          <ShieldIllustration />
        </div>
      </div>
    </section>
  );
}

/* ── Download CTA ────────────────────────────────────────────────────── */
function DownloadCTA() {
  return (
    <section className="section-cta">
      <div className="landing-container landing-center">
        <div data-animate="scale">
          <NaiaMark size={56} />
          <h2 className="cta-headline">Prêt à allumer la curiosité ?</h2>
          <p className="cta-sub">
            Rejoignez les établissements, familles et enseignants qui font confiance à Naïa-Edu au quotidien.
          </p>
          <div className="cta-actions">
            <Link to="/sign-in" className="cta-primary large">
              Parler avec Naïa →
            </Link>
          </div>
          <p className="cta-footnote">Gratuit · Pas de carte bancaire requise</p>
        </div>
      </div>
    </section>
  );
}

/* ── Footer ──────────────────────────────────────────────────────────── */
function LandingFooter() {
  return (
    <footer className="landing-footer">
      <div className="landing-container footer-inner">
        <div className="landing-foot-brand">
          <NaiaLogo height={22} />
        </div>
        <p className="landing-foot-copy">
          © 2025-2026 Naïa-Edu. Tous droits réservés.
        </p>
        <nav className="landing-foot-nav">
          <Link to="/sign-in" className="footer-link">Connexion</Link>
          <a href="mailto:projet.naia.edu@gmail.com" className="footer-link">Contact</a>
          <Link to="/confidentialite" className="footer-link">Confidentialité</Link>
          <Link to="/cgu" className="footer-link">CGU</Link>
        </nav>
      </div>
    </footer>
  );
}

/* ── Helpers ─────────────────────────────────────────────────────────── */
/* ── Trust bar icons ─────────────────────────────────────────────────── */

/* ── NaiaMark ────────────────────────────────────────────────────────── */
function NaiaMark({ size = 30, light = false }: { size?: number; light?: boolean }) {
  return (
    <svg width={size} height={size * 0.75} viewBox="0 0 32 24" aria-hidden focusable="false" className="landing-svg">
      <path d="M8 13 L24 13 L24 18.5 C24 21 20.4 22.5 16 22.5 C11.6 22.5 8 21 8 18.5 Z"
        fill={light ? 'rgba(255,255,255,0.55)' : 'var(--naia-blue)'} opacity="0.55" />
      <path d="M16 4 L30 11 L16 18 L2 11 Z"
        fill={light ? 'var(--naia-card)' : 'var(--naia-blue)'} />
      <path d="M30 11 V19.5" fill="none"
        stroke={light ? 'rgba(255,255,255,0.8)' : 'var(--naia-green)'}
        strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="30" cy="20.6" r="1.8"
        fill={light ? 'rgba(255,255,255,0.8)' : 'var(--naia-green)'} />
    </svg>
  );
}

/* ── Chat mockup ─────────────────────────────────────────────────────── */
function ChatMockup() {
  const messages = [
    { from: 'user', text: 'Pourquoi le ciel est bleu ?' },
    { from: 'naia', text: 'Super question ! La lumière du soleil contient toutes les couleurs. Les molécules d\'air diffusent surtout le bleu…' },
    { from: 'user', text: 'Et la nuit c\'est pourquoi c\'est noir ?' },
    { from: 'naia', text: 'Excellente suite ! La nuit, le soleil éclaire l\'autre côté de la Terre. C\'est le paradoxe d\'Olbers ! 🌙' },
  ];
  return (
    <div className="mockup-phone">
      <div className="mockup-notch" aria-hidden />
      <div className="mockup-screen">
        <div className="mockup-header">
          <div className="mockup-avatar"><NaiaMark size={18} /></div>
          <span className="mockup-name">Naïa</span>
          <span className="mockup-status">● en ligne</span>
        </div>
        <div className="mockup-messages">
          {messages.map((m, i) => (
            <div key={i} className={`mockup-bubble ${m.from}`} style={{ animationDelay: `${0.8 + i * 0.35}s` }}>
              {m.text}
            </div>
          ))}
          <div className="mockup-typing">
            <span /><span /><span />
          </div>
        </div>
        <div className="mockup-input">
          <span>Pose ta question…</span>
          <button type="button" aria-label="Envoyer" className="mockup-send">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Shield ──────────────────────────────────────────────────────────── */
function ShieldIllustration() {
  return (
    <div className="shield-wrap">
      <svg width="160" height="184" viewBox="0 0 160 184" aria-hidden>
        <path d="M80 8 L152 36 L152 100 C152 140 80 176 80 176 C80 176 8 140 8 100 L8 36 Z"
          fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.16)" strokeWidth="1.5" />
        <path d="M80 28 L136 52 L136 100 C136 130 80 158 80 158 C80 158 24 130 24 100 L24 52 Z"
          fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
        <text x="80" y="112" textAnchor="middle" fontSize="52" fill="rgba(255,255,255,0.88)">🔒</text>
      </svg>
    </div>
  );
}

/* ── CSS ─────────────────────────────────────────────────────────────── */
